import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { logActivity } from '../../../services/teamService';

const OrganizationTab = ({ organization, readOnly = false, refreshOrganization }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    logoUrl: ''
  });

  useEffect(() => {
    if (organization) {
      // Support both snake_case (DB) and camelCase field names
      setFormData({
        name: organization?.name || '',
        contactEmail: organization?.contact_email || organization?.contactEmail || '',
        contactPhone: organization?.contact_phone || organization?.contactPhone || '',
        address: organization?.address || '',
        city: organization?.city || '',
        state: organization?.state || '',
        zipCode: organization?.zip_code || organization?.zipCode || '',
        country: organization?.country || 'United States',
        logoUrl: organization?.logo_url || organization?.logoUrl || ''
      });
    }
  }, [organization]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (readOnly) return;
    setLoading(true);
    try {
      // Map camelCase form fields to snake_case DB columns
      const updates = {
        name: formData?.name,
        contact_email: formData?.contactEmail,
        contact_phone: formData?.contactPhone,
        address: formData?.address,
        city: formData?.city,
        state: formData?.state,
        zip_code: formData?.zipCode,
        country: formData?.country,
        logo_url: formData?.logoUrl,
        updated_at: new Date()?.toISOString()
      };

      const { error } = await supabase
        ?.from('organizations')
        ?.update(updates)
        ?.eq('id', organization?.id);

      if (error) throw error;

      // Log the activity
      await logActivity(
        'update',
        'organization',
        organization?.id,
        { updated_fields: Object.keys(formData) }
      );

      alert('Organization settings updated successfully');
      setIsEditing(false);
      // Refresh org data in AuthContext so parent has fresh data
      if (typeof refreshOrganization === 'function') {
        await refreshOrganization();
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      alert('Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (organization) {
      setFormData({
        name: organization?.name || '',
        contactEmail: organization?.contact_email || organization?.contactEmail || '',
        contactPhone: organization?.contact_phone || organization?.contactPhone || '',
        address: organization?.address || '',
        city: organization?.city || '',
        state: organization?.state || '',
        zipCode: organization?.zip_code || organization?.zipCode || '',
        country: organization?.country || 'United States',
        logoUrl: organization?.logo_url || organization?.logoUrl || ''
      });
    }
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Company Profile */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Company Profile</h3>
            <p className="text-sm text-muted-foreground">Manage your organization's information</p>
          </div>
          {!isEditing && !readOnly && (
            <Button onClick={() => setIsEditing(true)} variant="outline">
              <Icon name="Edit" size={18} />
              <span>Edit</span>
            </Button>
          )}
          {readOnly && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg text-xs text-muted-foreground">
              <Icon name="Lock" size={14} />
              <span>Read-only</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Company Name */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Company Name *
            </label>
            <Input
              type="text"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              disabled={!isEditing || readOnly}
              required
              placeholder="Your Company Name"
            />
          </div>

          {/* Logo URL */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Logo URL
            </label>
            <Input
              type="url"
              value={formData?.logoUrl}
              onChange={(e) => handleInputChange('logoUrl', e?.target?.value)}
              disabled={!isEditing || readOnly}
              placeholder="https://example.com/logo.png"
            />
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Contact Email
              </label>
              <Input
                type="email"
                value={formData?.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e?.target?.value)}
                disabled={!isEditing || readOnly}
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Contact Phone
              </label>
              <Input
                type="tel"
                value={formData?.contactPhone}
                onChange={(e) => handleInputChange('contactPhone', e?.target?.value)}
                disabled={!isEditing || readOnly}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Street Address
            </label>
            <Input
              type="text"
              value={formData?.address}
              onChange={(e) => handleInputChange('address', e?.target?.value)}
              disabled={!isEditing || readOnly}
              placeholder="123 Main Street"
            />
          </div>

          {/* City, State, Zip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                City
              </label>
              <Input
                type="text"
                value={formData?.city}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                disabled={!isEditing || readOnly}
                placeholder="San Francisco"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                State
              </label>
              <Input
                type="text"
                value={formData?.state}
                onChange={(e) => handleInputChange('state', e?.target?.value)}
                disabled={!isEditing || readOnly}
                placeholder="CA"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Zip Code
              </label>
              <Input
                type="text"
                value={formData?.zipCode}
                onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                disabled={!isEditing || readOnly}
                placeholder="94102"
              />
            </div>
          </div>

          {/* Country */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Country
            </label>
            <Input
              type="text"
              value={formData?.country}
              onChange={(e) => handleInputChange('country', e?.target?.value)}
              disabled={!isEditing || readOnly}
              placeholder="United States"
            />
          </div>

          {/* Action Buttons - only shown for non-readOnly users in edit mode */}
          {isEditing && !readOnly && (
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Icon name="Loader2" size={18} className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={18} />
                    <span>Save Changes</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </div>

      {/* Organization Preferences */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div>
              <p className="text-sm font-medium text-foreground">Email Notifications</p>
              <p className="text-xs text-muted-foreground">Receive email updates for important events</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked disabled={readOnly} />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div>
              <p className="text-sm font-medium text-foreground">Activity Logging</p>
              <p className="text-xs text-muted-foreground">Track all user activities and changes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked disabled={readOnly} />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationTab;